import { Graphics } from 'pixi.js';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

const WAYPOINT_RADIUS = 4; // Radius of waypoint circles

/**
 * Renders debug visualization for entity path waypoints.
 * Shows small circles at each waypoint, colored per-entity.
 * Toggled via the Debug Panel.
 */
export class DebugPathRenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['Path', 'DebugColor', 'Transform'];

  // Store graphics per entity to avoid shared state conflicts
  private pathGraphics: Map<number, Graphics> = new Map();

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    // Check debug toggle
    const existingGraphics = this.pathGraphics.get(entityId);

    if (!this.clientServices.debug.state.showPathMarkers) {
      // Hide existing graphics if toggle is off
      if (existingGraphics) {
        existingGraphics.visible = false;
      }
      return;
    }

    const path = world.getComponent(entityId, 'Path')!;
    const debugColor = world.getComponent(entityId, 'DebugColor')!;
    const transform = world.getComponent(entityId, 'Transform')!;

    // Get local player's scene to filter rendering
    const localEntityId = this.clientServices.localPlayer.entityId;
    const localPlayerTransform = localEntityId
      ? world.getComponent(localEntityId, 'Transform')
      : null;
    const currentSceneId = localPlayerTransform?.sceneId;

    // Only render for entities in the same scene as player
    if (!currentSceneId || transform.sceneId !== currentSceneId) {
      return;
    }

    let graphics = existingGraphics;
    if (!graphics) {
      graphics = new Graphics();
      graphics.zIndex = 10000;
      this.clientServices.render.stage?.addChild(graphics);
      this.pathGraphics.set(entityId, graphics);
    }

    graphics.visible = true;
    graphics.clear();

    // Draw circles at each waypoint
    for (let i = 0; i < path.waypoints.length; i++) {
      const waypoint = path.waypoints[i];

      // Only draw waypoints in current scene
      if (waypoint.sceneId !== currentSceneId) {
        continue;
      }

      const isCurrent = i === path.currentWaypointIndex;
      const alpha = isCurrent ? 1 : 0.5;
      const radius = isCurrent ? WAYPOINT_RADIUS * 1.5 : WAYPOINT_RADIUS;

      graphics.circle(waypoint.x, waypoint.y, radius);
      graphics.fill({ color: debugColor.color, alpha: alpha * 0.7 });
      graphics.stroke({ color: debugColor.color, width: 1, alpha });
    }
  }

  /**
   * Cleanup graphics for entities that no longer exist or lost Path component.
   */
  override run(deltaTime?: number): void {
    const world = this.getWorld();
    // Cleanup stale graphics before processing
    for (const [entityId, graphics] of this.pathGraphics) {
      const hasPath = world.getComponent(entityId, 'Path');
      if (!hasPath) {
        graphics.destroy();
        this.pathGraphics.delete(entityId);
      }
    }

    super.run(deltaTime);
  }
}
